import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Chip,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import Modal from '../../../common/components/Modal/Modal';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import EditUser from './EditUser';
import { useUsers } from '../hooks/useUsers';
import { Module } from '../hooks/useUsers';
import axios from 'axios';
import { 
  themeColors,
  SegmentedControlContainer,
  SegmentButton,
  SearchField 
} from './UserManagement.styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  UserRoles, 
  AccountTypes, 
  AccountTypeLabels, 
  UserStatus, 
  PPOValues, 
  ModuleNames, 
  ModuleUrls, 
} from '../../../common/constants/userConstants';
import { useDesignatedOperators } from '../hooks/useDesignatedOperators';
import { useUser, userQueryKey } from '../../../hooks/useUser';
import { useQueryClient } from '@tanstack/react-query';

// Match EditUser interface for compatibility
interface UserData {
  id?: string;
  samId?: string;
  email: string;
  accountType?: string;
  role?: string;
  modules: Module[];
  username?: string;
  status?: string;
  // Legacy fields for backward compatibility
  type?: string;
  designatedOperator?: string;
  userType?: string;
}

// Updated to align with Module interface in EditUser.tsx
interface UserModuleData {
  id?: number;
  name: string;
  url: string;
  enabled: boolean;
  isAdminDisabled?: boolean;
  permissions: {
    id?: number;
    name: string;
    url: string;
    enabled: boolean;
    isAdminDisabled?: boolean;
  }[];
}

interface UserManagementProps {
  isCodesTab: boolean;
}

function UserManagement({ isCodesTab }: UserManagementProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get current user info with data destructuring pattern
  const { data: userInfo, isLoading: userLoading, refreshUserData } = useUser();
  
  // Check if user has admin role using constants
  const isAdmin = userInfo?.role === UserRoles.ADMIN;
  
  // Parse query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  
  // Function to get short module name
  const getModuleShortName = (moduleName: string) => {
    const shortNameMap: Record<string, string> = {
      [ModuleNames.ENO1_SAMPLING]: 'ENO1',
      [ModuleNames.E53_SAMPLING]: 'E53',
      [ModuleNames.SAMPLING_PLANNING]: 'SAMPLING',
      [ModuleNames.E55_WEIGHTS]: 'E55',
      [ModuleNames.E09_IBRS]: 'E09',
      [ModuleNames.IPC_TD_ACCOUNTING]: 'IPC_ACC',
      [ModuleNames.TD_ACCOUNTING]: 'TD_ACC'
    };
    return shortNameMap[moduleName] || moduleName;
  };
  
  // Module order priority
  const moduleOrderPriority: Record<string, number> = {
    'ENO1': 1,
    'E53': 2,
    'SAMPLING': 3,
    'E55': 4,
    'E09': 5,
    'IPC_ACC': 6,
    'TD_ACC': 7
  };
  
  // Initialize state from URL query parameters or defaults
  const [searchTerm, setSearchTerm] = useState(queryParams.get('search') || '');
  const [debouncedEmail, setDebouncedEmail] = useState(''); // Separate state for debounced email
  const [isSearching, setIsSearching] = useState(false); // Track when search is in progress
  const [selectedDesignatedOperator, setSelectedDesignatedOperator] = useState(queryParams.get('designatedOperator') || PPOValues.ALL);
  
  // Store both the display label for UI and the actual value for API
  const [selectedTab, setSelectedTab] = useState(queryParams.get('tab') || 'all');
  // Map tab display values to actual accountType values for API
  const getAccountTypeValue = (tab: string): string | undefined => {
    if (tab === 'all') return undefined;
    if (tab === AccountTypeLabels[AccountTypes.IPC_USER]) return AccountTypes.IPC_USER;
    if (tab === AccountTypeLabels[AccountTypes.MAILBOX]) return AccountTypes.MAILBOX;
    return undefined;
  };
  
  const [pageSize, setPageSize] = useState<number>(
    parseInt(queryParams.get('size') || '10', 10)
  );
  const [page, setPage] = useState<number>(
    parseInt(queryParams.get('page') || '0', 10)
  );
  
  // User state
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  
  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  
  // Alert state for API operations
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Create references for the EditUser components with proper types
  const editUserRef = React.useRef<{ handleSave: () => void; hasErrors: boolean } | null>(null);
  const addUserRef = React.useRef<{ handleSave: () => void; hasErrors: boolean } | null>(null);

  // Add state to track form errors
  const [editFormHasErrors, setEditFormHasErrors] = useState(false);
  const [addFormHasErrors, setAddFormHasErrors] = useState(false);

  // Effect to check edit form errors when the component renders or reference changes
  useEffect(() => {
    // One-time check for errors
    if (editUserRef.current) {
      setEditFormHasErrors(editUserRef.current.hasErrors);
    }
  }, [isEditModalOpen]); // Only recheck when modal opens

  // Effect to check add form errors when the component renders or reference changes
  useEffect(() => {
    // One-time check for errors
    if (addUserRef.current) {
      setAddFormHasErrors(addUserRef.current.hasErrors);
    }
  }, [isAddModalOpen]); // Only recheck when modal opens
  
  // Fetch designated operators using the new hook - only enable for admin users
  const { data: operatorsData, isLoading: isLoadingDOs } = useDesignatedOperators({}, isAdmin);
  const designatedOperators = operatorsData?.content || [];
  
  // Update URL query parameters when filters change
  const updateUrlParams = React.useCallback(() => {
    const params = new URLSearchParams(location.search);
    
    // Preserve activeTab parameter if it exists
    const activeTab = params.get('activeTab');
    
    // Reset the params and add our filters
    params.delete('search');
    params.delete('designatedOperator');
    params.delete('tab');
    params.delete('size');
    params.delete('page');
    
    // Add query parameters that will be used directly for API calls
    if (searchTerm) params.set('search', searchTerm);
    if (isAdmin && selectedDesignatedOperator !== PPOValues.ALL) params.set('designatedOperator', selectedDesignatedOperator);
    if (selectedTab !== 'all') params.set('tab', selectedTab); // Will be sent as accountType to API
    if (pageSize !== 10) params.set('size', pageSize.toString());
    if (page !== 0) params.set('page', page.toString());
    
    // Restore activeTab parameter if it was present
    if (activeTab) {
      params.set('activeTab', activeTab);
    }
    
    // Only update URL if parameters have changed
    if (location.search !== `?${params.toString()}`) {
      navigate({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      }, { replace: true });
    }
  }, [searchTerm, selectedDesignatedOperator, selectedTab, pageSize, page, navigate, location.pathname, location.search]);
  
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);
  
  // Fetch users using the hook with parameters from URL
  const { data: usersData, isLoading, error, refetch } = useUsers({ 
    page: page, 
    size: pageSize,
    // Designated operator filter is applied via the API
    ...(isAdmin && selectedDesignatedOperator !== PPOValues.ALL 
      ? { designatedOperator: selectedDesignatedOperator } 
      : {}),
    // Email filtering handled by backend - only send when value is debounced
    ...(debouncedEmail ? { email: debouncedEmail } : {}),
    // Account type filtering now handled by backend API with actual enum values
    ...(selectedTab !== 'all' ? { accountType: getAccountTypeValue(selectedTab) } : {})
  });
  
  // Transform API data to match our component's data structure
  const transformedUsers = usersData?.content.map(user => {
    // Detect IPC user type accounting for possible variations in case
    const isIpcUser = (user.accountType || '').toUpperCase().includes('IPC');
    
    return {
      id: user.id || '',
      samId: user.samId || '',
      email: user.email,
      accountType: user.accountType,
      role: user.role,
      modules: user.modules,
      username: user.samId || (user.email ? user.email.split('@')[0] : ''),
      // Legacy field mappings for UI compatibility
      type: isIpcUser ? AccountTypeLabels[AccountTypes.IPC_USER] : AccountTypeLabels[AccountTypes.MAILBOX],
      // Use user's designatedOperator if available, otherwise fallback to role-based logic
      designatedOperator: (user as any).designatedOperator || (user as any).ppo || (user.role === UserRoles.ADMIN ? PPOValues.IPC : PPOValues.ATA),
      userType: user.role,
      status: user.status
    };
  }) || [];

  // No need to filter by account type anymore as it's handled by the backend
  // Just use the transformed users directly
  const filteredUsers = transformedUsers;

  // Implement debounce for email search with improved wait behavior
  useEffect(() => {
    // Create a timer that will update the debounced value after user stops typing
    const timer = setTimeout(() => {
      setDebouncedEmail(searchTerm);
      setIsSearching(false);
      // Reset to first page when email filter changes
      setPage(0);
    }, 800); // Longer timeout to ensure user has finished typing
    
    // Show loading indicator when user is typing
    if (searchTerm !== debouncedEmail) {
      setIsSearching(true);
    }
    
    // Clear the timer on every searchTerm change
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedEmail]);
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleTabChange = (newValue: string) => {
    setSelectedTab(newValue);
    // Reset to first page when account type tab changes
    // This will trigger a new API request with the updated account type filter
    setPage(0);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setIsAddModalOpen(false);
    setIsDeleteConfirmOpen(false);
    setSelectedUser(null);
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, open: false });
  };

  const handleSaveUser = async (updatedUser: UserData) => {
    try {
      // Create a base user data object with required fields
      const userData: Record<string, any> = {
        accountType: updatedUser.accountType,
        role: updatedUser.role,
        designatedOperator: updatedUser.designatedOperator,
        status: UserStatus.ACTIVE // Required field for the API
      };
      
      // Only add email if it's not empty
      if (updatedUser.email) {
        userData.email = updatedUser.email;
      }

      // For add mode, exclude all IDs
      if (isAddModalOpen) {
        // Create a new user object WITHOUT any IDs
        const addUserData = {
          ...userData,
          samId: updatedUser.samId || null,
          designatedOperator: updatedUser.designatedOperator,
          modules: updatedUser.modules.map(module => {
            // Use type assertion to access isAdminDisabled
            const typedModule = module as unknown as { isAdminDisabled?: boolean, permissions: { isAdminDisabled?: boolean }[] };
            return {
              name: module.name,
              url: module.url,
              enabled: module.enabled,
              isAdminDisabled: typedModule.isAdminDisabled || false,
              permissions: module.permissions.map((permission: any) => ({
                name: permission.name,
                url: permission.url,
                enabled: permission.enabled,
                isAdminDisabled: permission.isAdminDisabled || false
              }))
            };
          })
        };

        // Add new user with POST request
        await axios.post('/user', addUserData);
        setAlert({
          open: true,
          message: 'User added successfully',
          severity: 'success'
        });
      } else if (isEditModalOpen && selectedUser) {
        // For edit mode, include the existing IDs
        const editUserData = {
          id: updatedUser.id || null,
          samId: updatedUser.samId || null,
          ...userData,
          designatedOperator: updatedUser.designatedOperator,
          modules: updatedUser.modules.map(module => {
            // Use type assertion to access isAdminDisabled
            const typedModule = module as unknown as { isAdminDisabled?: boolean, permissions: { isAdminDisabled?: boolean }[] };
            return {
              id: module.id || 0,
              name: module.name,
              url: module.url,
              enabled: module.enabled,
              isAdminDisabled: typedModule.isAdminDisabled || false,
              permissions: module.permissions.map((permission: any) => ({
                id: permission.id || 0,
                name: permission.name,
                url: permission.url,
                enabled: permission.enabled,
                isAdminDisabled: permission.isAdminDisabled || false
              }))
            };
          })
        };

        // Update existing user with PUT request
        const response = await axios.put('/user', editUserData);
        setAlert({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
        
        // Check if the updated user is the current logged-in user
        // Compare by id, email, or samId to catch all cases
        const isCurrentUser = 
          updatedUser.id === userInfo?.id || 
          updatedUser.email === userInfo?.email ||
          updatedUser.samId === userInfo?.samId;
        
        if (isCurrentUser) {
          // If so, update the cache directly with the response data
          // This ensures immediate update for all components reading from cache
          if (response?.data) {
            queryClient.setQueryData(userQueryKey, response.data);
          }
          // Also invalidate and refetch to ensure consistency
          await refreshUserData();
          // Force refetch to ensure components with enabled=false also get updated data
          // This will trigger UserInformationProvider to refetch and update the cache
          await queryClient.refetchQueries({ queryKey: userQueryKey });
        }
      }
      
      // Refresh the user list
      refetch();
      
      // Close the modal
      handleCloseModal();
    } catch (error) {
      console.error('Error saving user:', error);
      
      // Extract error message from axios error response
      let errorMessage = 'Error saving user';
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // Functions to handle the save button click in the Modals
  const handleEditModalSave = () => {
    if (editUserRef.current) {
      editUserRef.current.handleSave();
    }
  };

  const handleAddModalSave = () => {
    if (addUserRef.current) {
      addUserRef.current.handleSave();
    }
  };

  const handleAddUser = () => {
    // Get current user's modules and permissions
    if (!userInfo || !userInfo.modules) {
      // Show an error if user info is not available
      setAlert({
        open: true,
        message: 'Could not retrieve your permissions. Please try again.',
        severity: 'error'
      });
      return;
    }

    // Initialize modules based on current user's permissions
    // This ensures new users can only receive permissions that the current user has access to
    // The principle is: users cannot grant permissions they don't have themselves
    // This prevents privilege escalation where users could create new accounts with higher privileges
    const userModules: UserModuleData[] = userInfo.modules.map(module => {
      const moduleName = module.name;
      const moduleUrl = module.url;
      
      // Extract enabled permissions from the current user
      const enabledPermissions = module.permissions
        .filter(permission => permission.enabled)
        .map(permission => ({
          name: permission.name,
          url: permission.url,
          enabled: true,
          isAdminDisabled: false
        }));
      
      return {
        name: moduleName,
        url: moduleUrl,
        enabled: module.enabled,
        isAdminDisabled: false,
        permissions: enabledPermissions
      };
    });

    setSelectedUser({
      samId: '',
      email: '',
      accountType: AccountTypes.IPC_USER, // Default to IPC_USER type
      role: UserRoles.USER, // Default to regular USER role
      modules: userModules as unknown as Module[], // Type assertion to handle the mismatch
      status: UserStatus.ACTIVE,
      // Legacy fields for UI compatibility
      type: AccountTypeLabels[AccountTypes.IPC_USER],
      designatedOperator: userInfo?.designatedOperator, // Default designatedOperator
      userType: 'User'
    });
    setIsAddModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedUser?.id) {
      try {
        // Delete user API call
        await axios.delete(`/user/${selectedUser.id}`);
        
        setAlert({
          open: true,
          message: 'User deleted successfully',
          severity: 'success'
        });
        
        // Refresh the user list
        refetch();
        
        // If the deleted user was the current user, refresh the user data
        if (selectedUser.id === userInfo?.id || selectedUser.email === userInfo?.email) {
          await refreshUserData();
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        
        // Extract error message from axios error response
        let errorMessage = 'Error deleting user';
        if (axios.isAxiosError(error) && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
        
        setAlert({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
    
    setIsDeleteConfirmOpen(false);
    setSelectedUser(null);
  };
  
  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setSelectedUser(null);
  };

  const getModuleChipColor = (module: string) => {
    // Map of short module names to their colors with updated color codes
    const moduleColorMap: Record<string, { bg: string, color: string }> = {
      'ENO1': { bg: '#00ACE7', color: 'white' },
      'E53': { bg: '#063142', color: 'white' },
      'SAMPLING': { bg: '#6A848E', color: 'white' },
      'E55': { bg: '#49A942', color: 'white' },
      'E09': { bg: '#575A9C', color: 'white' },
      'IPC_ACC': { bg: '#FAA918', color: 'white' },
      'TD_ACC': { bg: '#93620A', color: 'white' },
      // Aliases for underscores vs spaces
      'IPC ACC': { bg: '#FAA918', color: 'white' },
      'TD ACC': { bg: '#93620A', color: 'white' },
      // Role colors
      'ADMIN': { bg: '#CF1839', color: 'white' },
      'SUPER_USER': { bg: '#00B6BA', color: 'white' },
      // Fallback for any other modules
      'DEFAULT': { bg: themeColors.light, color: themeColors.navy }
    };
    
    return moduleColorMap[module] || moduleColorMap.DEFAULT;
  };

  const [designatedOperatorAnchorEl, setDesignatedOperatorAnchorEl] = useState<null | HTMLElement>(null);
  const designatedOperatorOpen = Boolean(designatedOperatorAnchorEl);
  
  const handleDesignatedOperatorClick = (event: React.MouseEvent<HTMLElement>) => {
    setDesignatedOperatorAnchorEl(event.currentTarget);
  };
  
  const handleDesignatedOperatorClose = () => {
    setDesignatedOperatorAnchorEl(null);
  };
  
  const handleDesignatedOperatorSelect = (designatedOperator: string) => {
    setSelectedDesignatedOperator(designatedOperator);
    handleDesignatedOperatorClose();
    // Reset to first page when PPO filter changes
    setPage(0);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    // Reset to first page when page size changes
    setPage(0);
  };

  // Functions to handle error state updates from EditUser
  const handleEditUserErrorChange = (hasErrors: boolean) => {
    setEditFormHasErrors(hasErrors);
  };

  const handleAddUserErrorChange = (hasErrors: boolean) => {
    setAddFormHasErrors(hasErrors);
  };

  // Handle pagination page changes
  const handlePageChange = (newPage: number) => {
    // Ensure page is within valid range
    if (newPage >= 0 && (!usersData?.totalPages || newPage < usersData.totalPages)) {
      setPage(newPage);
    }
  };

  // Handle designated operator selection from dropdown
  const handleDesignatedOperatorChange = (event: SelectChangeEvent<string>) => {
    setSelectedDesignatedOperator(event.target.value);
    // Reset to first page when DO filter changes
    setPage(0);
  };

  if (isLoading || userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }} data-testid="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }} data-testid="error-container">
        <Typography color="error">
          {t('common.error', 'An error occurred while loading users.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2 }} data-testid="user-management-container">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }} data-testid="user-management-controls">        
        <FormControl 
          variant="outlined" 
          size="small" 
          sx={{ 
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              '&:hover fieldset': {
                borderColor: themeColors.turq,
              },
            }
          }}
        >
          <InputLabel id="items-per-page-label" sx={{ color: themeColors.navy }}>
            {t('common.itemsPerPage', 'Items per page')}
          </InputLabel>
          <Select
            labelId="items-per-page-label"
            id="items-per-page"
            value={pageSize}
            onChange={handlePageSizeChange}
            label={t('common.itemsPerPage', 'Items per page')}
            data-testid="items-per-page-select"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>

          {/* Designated Operator Dropdown - Only visible for admin users */}
          {isAdmin && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: themeColors.turq,
                  },
                }
              }}
            >
              <InputLabel id="designated-operator-label" sx={{ color: themeColors.navy }}>
                {t('profile.table.designatedOperator', 'Designated Operator')}
              </InputLabel>
              <Select
                labelId="designated-operator-label"
                id="designated-operator"
                value={selectedDesignatedOperator}
                onChange={handleDesignatedOperatorChange}
                label={t('profile.table.designatedOperator', 'Designated Operator')}
                data-testid="designated-operator-select"
                disabled={isLoadingDOs}
              >
                <MenuItem value={PPOValues.ALL}>{t('common.all', 'All')}</MenuItem>
                {isLoadingDOs ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="loading-operators">
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('common.loading', 'Loading...')}
                    </Box>
                  </MenuItem>
                ) : Array.isArray(designatedOperators) ? designatedOperators.map((operator) => (
                  <MenuItem key={operator.id} value={operator.name}>
                    {operator.name}
                  </MenuItem>
                )) : (
                  <MenuItem disabled>
                    {t('common.error', 'Error loading operators')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} data-testid="filter-controls">
          <Box data-testid="tab-controls">
            <SegmentedControlContainer>
              {/* Account type tabs - will filter using backend API with AccountTypes enum values */}
              <SegmentButton 
                active={selectedTab === 'all'} 
                onClick={() => handleTabChange('all')}
                data-testid="tab-all"
              >
                All
              </SegmentButton>
              <SegmentButton 
                active={selectedTab === AccountTypeLabels[AccountTypes.IPC_USER]} 
                onClick={() => handleTabChange(AccountTypeLabels[AccountTypes.IPC_USER])}
                data-testid="tab-ipc-user"
                title={`Filter by ${AccountTypes.IPC_USER}`}
              >
                {AccountTypeLabels[AccountTypes.IPC_USER]}
              </SegmentButton>
              <SegmentButton 
                active={selectedTab === AccountTypeLabels[AccountTypes.MAILBOX]} 
                onClick={() => handleTabChange(AccountTypeLabels[AccountTypes.MAILBOX])}
                data-testid="tab-mailbox"
                title={`Filter by ${AccountTypes.MAILBOX}`}
              >
                {AccountTypeLabels[AccountTypes.MAILBOX]}
              </SegmentButton>
            </SegmentedControlContainer>
          </Box>
          
          <SearchField
            placeholder={t('common.searchSamIdOrEmail', 'Search by Email')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            data-testid="user-search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}
            sx={{ width: '240px' }}
          />
        </Box>
      </Box>
      <TableContainer sx={{ width: '100%'}}>
        <Table 
          sx={{ 
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0 8px'
          }} 
          aria-label="user management table" 
          data-testid="user-management-table"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: themeColors.navy, fontWeight: 600, border: 'none', pl: '12px' }}>{t('profile.table.user', 'User')}</TableCell>
              <TableCell sx={{ color: themeColors.navy, fontWeight: 600, border: 'none', pl: '12px' }}>{t('profile.table.designatedOperator', 'DO')}</TableCell>
              <TableCell sx={{ color: themeColors.navy, fontWeight: 600, border: 'none', pl: '12px' }}>{t('profile.table.status', 'Status')}</TableCell>
              <TableCell sx={{ color: themeColors.navy, fontWeight: 600, border: 'none', pl: '12px' }}>{t('profile.table.modules', 'Modules')}</TableCell>
              <TableCell align="right" sx={{ color: themeColors.navy, fontWeight: 600, border: 'none', pr: '8px' }}>{t('profile.table.actions', '')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
              <TableRow
                key={user.id}
                sx={{ 
                  '&:last-child td, &:last-child th': { border: 0 },
                  backgroundColor: '#E7EFF5',
                  height: '34px',
                  '& td, & th': {
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    paddingLeft: '12px',
                    paddingRight: '8px',
                    border: 'none'
                  },
                 
                }}
                data-testid={`user-row-${user.id}`}
              >
                <TableCell sx={{ color: themeColors.navy }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid={`user-info-${user.id}`}>
                    {user.type === AccountTypeLabels[AccountTypes.IPC_USER] ? (
                      <PersonIcon sx={{ mr: 1, fontSize: 18, color: themeColors.navy }} />
                    ) : (
                      <MailIcon sx={{ mr: 1, fontSize: 18, color: themeColors.navy }} />
                    )}
                    {user.email || user.samId || '-'}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: themeColors.navy }}>{user.designatedOperator}</TableCell>
                <TableCell sx={{ color: themeColors.navy }}>
                  <Chip
                    label={user.status || 'ACTIVE'}
                    size="small"
                    sx={{ 
                      backgroundColor: (() => {
                        switch(user.status) {
                          case 'CREATED':
                            return '#1890FF'; // Blue
                          case 'ACTIVE':
                            return '#52C41A'; // Green
                          case 'DEACTIVATED':
                            return '#FAAD14'; // Amber
                          case 'DELETED':
                            return '#F5222D'; // Red
                          default:
                            return '#52C41A'; // Default to green/active
                        }
                      })(),
                      color: 'white',
                      padding: '2px 8px',
                      fontSize: '11px',
                      height: '22px',
                      fontWeight: '700',
                      borderRadius: '4px',
                      '& .MuiChip-label': {
                        padding: '0 4px'
                      }
                    }}
                    data-testid={`status-chip-${user.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid={`modules-container-${user.id}`}>
                    {/* Display user role chip only for ADMIN and SUPER_USER roles */}
                    {user.role && (user.role === UserRoles.ADMIN || user.role === UserRoles.SUPER_USER) && (
                      <Chip
                        label={user.role.replace('_', ' ')}
                        size="small"
                        sx={{ 
                          backgroundColor: getModuleChipColor(user.role).bg, 
                          color: getModuleChipColor(user.role).color,
                          padding: '2px 8px',
                          fontSize: '11px',
                          height: '22px',
                          fontWeight: '700',
                          borderRadius: '4px',
                          '& .MuiChip-label': {
                            padding: '0 4px'
                          },
                          margin: '2px',
                          border: '1px solid transparent',
                          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                        data-testid={`role-chip-${user.role.toLowerCase()}`}
                      />
                    )}
                    {user.modules
                      .filter(module => module.enabled)
                      // Sort modules based on a predefined order
                      .sort((a, b) => {
                        const aShortName = getModuleShortName(a.name);
                        const bShortName = getModuleShortName(b.name);
                        
                        return (moduleOrderPriority[aShortName] || 999) - (moduleOrderPriority[bShortName] || 999);
                      })
                      .map((module, index) => {
                        // Convert module name to uppercase to match the getModuleChipColor cases
                        const moduleShortName = getModuleShortName(module.name);
                        const { bg, color } = getModuleChipColor(moduleShortName);
                        
                        return (
                          <Chip
                            key={index}
                            label={moduleShortName}
                            size="small"
                            sx={{ 
                              backgroundColor: bg, 
                              color: color,
                              padding: '2px 8px',
                              fontSize: '11px',
                              height: '22px',
                              fontWeight: '700',
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                padding: '0 4px'
                              },
                              margin: '2px',
                              border: '1px solid transparent',
                              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)'
                            }}
                            data-testid={`module-chip-${moduleShortName.toLowerCase()}`}
                          />
                        );
                      })}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ 
                 
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: 'auto' }} data-testid={`user-actions-${user.id}`}>
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setSelectedUser(user);
                        setIsEditModalOpen(true);
                      }}
                      data-testid={`user-edit-${user.id}`}
                      sx={{ 
                        color: themeColors.navy,
                        padding: '4px',
                        '&:hover': { color: themeColors.turq }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setSelectedUser(user);
                        setIsDeleteConfirmOpen(true);
                      }}
                      data-testid={`user-delete-${user.id}`}
                      sx={{ 
                        color: themeColors.navy,
                        padding: '4px',
                        '&:hover': { color: '#FF4D4F' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {t('common.noRecordsFound', 'No users found matching your criteria')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="user-management-footer">
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />}
          onClick={isCodesTab ? undefined : handleAddUser}
          data-testid="add-user-button"
          sx={{ 
            textTransform: 'none',
            borderColor: themeColors.turq,
            color: themeColors.turq,
            '&:hover': {
              borderColor: themeColors.turq,
              backgroundColor: 'rgba(0, 182, 186, 0.1)',
            }
          }}
        >
          {isCodesTab 
            ? t('profile.ipcCodes.addSharedMailbox', 'Add Shared Mailbox') 
            : t('profile.userManagement.addUser', 'Add User')}
        </Button>
        
        {/* Pagination controls - Only show if we have users or pagination data */}
        {(filteredUsers.length > 0 || (usersData?.totalElements && usersData.totalElements > 0)) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} data-testid="pagination-controls">
            {usersData?.totalElements !== undefined && (
              <Typography variant="body2" sx={{ mr: 1, color: themeColors.navy }}>
                {t('common.totalRecords', 'Total')}: {usersData.totalElements}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, color: themeColors.navy }}>
                {t('common.page', 'Page')}: {page + 1} 
                {usersData?.totalPages && ` / ${usersData.totalPages}`}
              </Typography>
              <Button
                variant="outlined"
                disabled={page === 0}
                onClick={() => handlePageChange(page - 1)}
                sx={{ 
                  minWidth: 40, 
                  height: 40,
                  p: '8px', 
                  borderRadius: '8px',
                  borderColor: themeColors.turq,
                  color: themeColors.turq,
                  '&:hover': {
                    borderColor: themeColors.turq,
                    backgroundColor: 'rgba(0, 182, 186, 0.1)',
                  }
                }}
                data-testid="prev-page-button"
              >
                &lt;
              </Button>
              <Button
                variant="outlined"
                disabled={Boolean(usersData?.last) || Boolean(usersData?.totalPages && page >= usersData.totalPages - 1)}
                onClick={() => handlePageChange(page + 1)}
                sx={{ 
                  minWidth: 40, 
                  height: 40,
                  p: '8px',
                  ml: 1,
                  borderRadius: '8px',
                  borderColor: themeColors.turq,
                  color: themeColors.turq,
                  '&:hover': {
                    borderColor: themeColors.turq,
                    backgroundColor: 'rgba(0, 182, 186, 0.1)',
                  }
                }}
                data-testid="next-page-button"
              >
                &gt;
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title={t('profile.userManagement.editUser', 'Edit User')}
        onApply={handleEditModalSave}
        applyText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="lg"
        disableSave={editFormHasErrors}
        data-testid="edit-user-modal"
      >
        <EditUser 
          user={selectedUser as any}
          onClose={handleCloseModal}
          onSave={handleSaveUser as any}
          ref={editUserRef}
          isAddMode={false}
          onErrorChange={handleEditUserErrorChange}
        />
      </Modal>

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        title={t('profile.userManagement.addUser', 'Add User')}
        onApply={handleAddModalSave}
        applyText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="lg"
        disableSave={addFormHasErrors}
        data-testid="add-user-modal"
      >
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              {t('profile.userManagement.permissionInheritance', 'New users can only be granted permissions that you already have. To grant additional permissions, contact an administrator.')}
            </Typography>
          </Alert>
        </Box>
        <EditUser 
          user={selectedUser as any}
          onClose={handleCloseModal}
          onSave={handleSaveUser as any}
          ref={addUserRef}
          isAddMode={true}
          onErrorChange={handleAddUserErrorChange}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        title={t('profile.userManagement.deleteUser', 'Delete User')}
        onApply={handleConfirmDelete}
        applyText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="sm"
        data-testid="delete-user-modal"
      >
        <Box sx={{ p: 2 }} data-testid="delete-confirmation-container">
          <Typography>
            {t('profile.userManagement.deleteConfirmation', 'Are you sure you want to delete this user?')}
          </Typography>
          {selectedUser && (
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
              {selectedUser.email}
            </Typography>
          )}
        </Box>
      </Modal>
      
      {/* Status Alert */}
      <NotificationSnackbar
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleAlertClose}
        data-testid={`alert-${alert.severity}`}
      />
    </Paper>
  );
}

export default UserManagement; 